import { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle2, ArrowRight, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const ApplyLoan = () => {
    const [searchParams] = useSearchParams();
    const [step, setStep] = useState('apply'); // apply | success | track
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [trackId, setTrackId] = useState('');
    const [trackResult, setTrackResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [response, setResponse] = useState(null);
    const [form, setForm] = useState({
        name: '', phone: '', email: '', pan: '',
        monthly_income: '', loan_amount_requested: '', purpose: '',
        category_id: '', product_id: '', city: '', pin_code: '',
        employment_type: 'salaried',
        referral_code: searchParams.get('ref') || '',
        consent_to_contact: true,
    });

    useEffect(() => {
        axios.get(`${API_URL}/api/public/categories`).then(r => setCategories(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (form.category_id) {
            axios.get(`${API_URL}/api/public/products?category_id=${form.category_id}`).then(r => setProducts(r.data || [])).catch(() => {});
        }
    }, [form.category_id]);

    const submit = async () => {
        if (!form.name || !form.phone || !form.consent_to_contact) {
            alert('Name, phone and consent are required');
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/public/apply`, {
                ...form,
                monthly_income: parseFloat(form.monthly_income) || null,
                loan_amount_requested: parseFloat(form.loan_amount_requested) || null,
            });
            setResponse(data);
            setStep('success');
        } catch (e) {
            alert(e.response?.data?.detail || 'Submission failed');
        } finally {
            setSubmitting(false);
        }
    };

    const trackApplication = async () => {
        if (!trackId) return;
        try {
            const { data } = await axios.get(`${API_URL}/api/public/lead-status/${trackId}`);
            setTrackResult(data);
        } catch (e) {
            alert(e.response?.data?.detail || 'Not found');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50" data-testid="public-apply-page">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 shadow-sm">
                <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
                    <img src="/digi-loans-logo.png" alt="DIGI-LOANS" className="h-10 w-10 object-contain" />
                    <div>
                        <h1 className="text-xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>DIGI-LOANS</h1>
                        <p className="text-[10px] text-slate-500 tracking-wider uppercase">Loan Simplified</p>
                    </div>
                    <div className="ml-auto flex gap-2">
                        <Button variant={step === 'apply' ? 'default' : 'outline'} size="sm" className={step === 'apply' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg' : 'rounded-lg'} onClick={() => setStep('apply')} data-testid="tab-apply">Apply</Button>
                        <Button variant={step === 'track' ? 'default' : 'outline'} size="sm" className={step === 'track' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg' : 'rounded-lg'} onClick={() => setStep('track')} data-testid="tab-track">Track</Button>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-10">
                {step === 'apply' && (
                    <Card className="rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                        <CardContent className="p-8">
                            <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Apply for a loan</h2>
                            <p className="text-slate-500 mb-6">Takes &lt; 2 minutes. We'll call you within 24 hours.</p>

                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Full name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="apply-name" /></div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Phone *</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="apply-phone" /></div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" /></div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">PAN</Label><Input value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} maxLength={10} className="rounded-lg mt-1 bg-slate-50 border-slate-200 uppercase" /></div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Monthly income (₹)</Label><Input type="number" value={form.monthly_income} onChange={e => setForm({ ...form, monthly_income: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" /></div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Loan amount needed (₹)</Label><Input type="number" value={form.loan_amount_requested} onChange={e => setForm({ ...form, loan_amount_requested: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="apply-amount" /></div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Employment</Label>
                                        <select value={form.employment_type} onChange={e => setForm({ ...form, employment_type: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1">
                                            {['salaried', 'self_employed', 'business', 'student'].map(v => <option key={v} value={v}>{v.replace('_', ' ')}</option>)}
                                        </select>
                                    </div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" /></div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">PIN code</Label><Input value={form.pin_code} onChange={e => setForm({ ...form, pin_code: e.target.value })} maxLength={6} className="rounded-lg mt-1 bg-slate-50 border-slate-200" /></div>
                                </div>

                                {categories.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div><Label className="text-xs font-bold uppercase text-slate-500">Loan category</Label>
                                            <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value, product_id: '' })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1" data-testid="apply-category">
                                                <option value="">— select —</option>
                                                {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                            </select>
                                        </div>
                                        {products.length > 0 && (
                                            <div><Label className="text-xs font-bold uppercase text-slate-500">Specific product</Label>
                                                <select value={form.product_id} onChange={e => setForm({ ...form, product_id: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1">
                                                    <option value="">— select —</option>
                                                    {products.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div><Label className="text-xs font-bold uppercase text-slate-500">Purpose / additional notes</Label><Textarea value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} rows={2} className="rounded-lg mt-1 bg-slate-50 border-slate-200" /></div>

                                {form.referral_code && (
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-700">
                                        Referred by <strong>{form.referral_code}</strong> — they'll be notified when you submit. 🤝
                                    </div>
                                )}

                                <label className="flex items-start gap-2 text-xs text-slate-600">
                                    <input type="checkbox" checked={form.consent_to_contact} onChange={e => setForm({ ...form, consent_to_contact: e.target.checked })} className="mt-0.5" data-testid="apply-consent" />
                                    I agree to be contacted by DIGI-LOANS regarding my loan application via phone, email, or WhatsApp.
                                </label>

                                <Button onClick={submit} disabled={submitting} className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm h-12 text-base font-bold" data-testid="apply-submit-btn">
                                    {submitting ? 'Submitting...' : <>Submit Application <ArrowRight className="h-4 w-4 ml-2" /></>}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {step === 'success' && response && (
                    <Card className="rounded-2xl shadow-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 to-blue-50 overflow-hidden">
                        <CardContent className="p-10 text-center">
                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-4">
                                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                            </div>
                            <h2 className="text-3xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Application received! 🎉</h2>
                            <p className="text-slate-600 mb-6">{response.message}</p>
                            <div className="bg-white border border-slate-200 rounded-xl p-4 inline-block mb-6">
                                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Tracking ID</p>
                                <p className="text-2xl font-mono font-bold text-[#2563eb] tabular-nums" data-testid="tracking-id">{response.tracking_id}</p>
                                {response.rm_assigned && <p className="text-xs text-slate-500 mt-2">Assigned RM: <strong>{response.rm_assigned}</strong></p>}
                            </div>
                            <p className="text-xs text-slate-500">Save this ID to track your application status.</p>
                            <div className="flex justify-center gap-2 mt-6">
                                <Button onClick={() => setStep('apply')} variant="outline" className="rounded-lg">Submit another</Button>
                                <Button onClick={() => { setTrackId(response.tracking_id); setStep('track'); }} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm">Track this application</Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {step === 'track' && (
                    <Card className="rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                        <CardContent className="p-8">
                            <h2 className="text-2xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Track your application</h2>
                            <div className="flex gap-2 mb-6">
                                <Input value={trackId} onChange={e => setTrackId(e.target.value.toUpperCase())} placeholder="Enter tracking ID (e.g. DLA1B2C3D4)" className="rounded-lg bg-slate-50 border-slate-200 font-mono" data-testid="track-input" />
                                <Button onClick={trackApplication} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="track-btn"><Search className="h-4 w-4" /></Button>
                            </div>
                            {trackResult && (
                                <div className="space-y-3" data-testid="track-result">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-slate-400">Application of</p>
                                            <p className="text-lg font-bold text-slate-900">{trackResult.name}</p>
                                        </div>
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-blue-100 text-blue-700">{(trackResult.status || 'new').replace('_', ' ')}</span>
                                    </div>
                                    {trackResult.rm_name && <p className="text-xs text-slate-500">RM: <strong>{trackResult.rm_name}</strong></p>}
                                    <div className="border-l-2 border-slate-200 pl-4 space-y-3 mt-4">
                                        {trackResult.status_history?.map((h, i) => (
                                            <div key={i} className="text-xs">
                                                <p className="font-bold text-slate-700 capitalize">{(h.status || '').replace('_', ' ')}</p>
                                                <p className="text-slate-500">{new Date(h.date).toLocaleString()}</p>
                                                {h.notes && <p className="text-slate-600 italic mt-0.5">{h.notes}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </main>

            <footer className="text-center py-6 text-xs text-slate-400">
                Powered by <span className="font-bold text-[#2563eb]">DIGI-LOANS</span> · All applications encrypted & secure
            </footer>
        </div>
    );
};

export default ApplyLoan;
